import { useAppDispatch } from "@redux";
import moment from "moment";
import { updateReservation } from "@redux";

interface ReservationUpdatePayload {
  id: number;
  room_ids: number[];
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
}

export const useCalendarDragAndDrop = () => {
  const dispatch = useAppDispatch();

  const handleUpdateReservation = async (updatedReservation: ReservationUpdatePayload) => {
    try {
      const resultAction = await dispatch(updateReservation(updatedReservation));
      //console.log('Update reservation result:', resultAction);
      
      // Check if the action was rejected
      if (updateReservation.rejected.match(resultAction)) {
        // Define a type for our expected error structure
        interface RoomConflictError {
          success: boolean;
          message: {
            conflicting_rooms: number[];
          };
          status: number;
          data: string;
        }
        
        const error = resultAction.payload;
        
        // Type guard function to check if the error matches our expected structure
        const isRoomConflictError = (err: unknown): err is RoomConflictError => {
          return !!err && 
                typeof err === 'object' && 
                'status' in err && 
                (err as any).status === 409 && 
                'message' in err && 
                typeof (err as any).message === 'object' && 
                'conflicting_rooms' in (err as any).message && 
                Array.isArray((err as any).message.conflicting_rooms);
        };
        
        if (isRoomConflictError(error)) {
          // Now TypeScript knows this is a RoomConflictError
          const conflictingRooms = error.message.conflicting_rooms.join(', ');
          
          // Show the alert in Japanese
          alert(`更新に失敗しました。次の部屋はその時間に使用中です：${conflictingRooms}`);
        } else {
          // Handle other errors
          alert((error as any)?.data || (error as any)?.message || '予約の更新中にエラーが発生しました。');
        }
      }
    } catch (error) {
      console.error('Failed to update reservation:', error);
      alert('予約の更新中にエラーが発生しました。');
    }
  };

  // Handle event drag and drop
  const handleEventDrop = ({ event, start, end }: any) => {
    const reservation = event.reservation;
    const reservationId = reservation.originalReservation?.id || reservation.id;
    const isRecurring = !!(reservation.originalReservation || reservation).recurring_frequency;
    //console.log('isRecurring', isRecurring);
    if (isRecurring) {
      alert('定期的なイベントを新しい日付にドラッグ アンド ドロップすることはできません。');
      return;
    }
    
    // Format new start and end times
    const newStartDate = moment(start).format('YYYY-MM-DD');
    const newEndDate = moment(end).format('YYYY-MM-DD');
    const newStartTime = moment(start).format('HH:mm');
    const newEndTime = moment(end).format('HH:mm');
    
    const updatedReservation = {
      id: reservationId,
      room_ids: reservation.room_ids,
      start_date: newStartDate,
      end_date: newEndDate,
      start_time: newStartTime,
      end_time: newEndTime,
    };

    handleUpdateReservation(updatedReservation);
  };
  
  // Handle event resize
  const handleEventResize = ({ event, start, end }: any) => {
    const reservation = event.reservation;
    const reservationId = reservation.originalReservation?.id || reservation.id;
    const isRecurring = !!(reservation.originalReservation || reservation).recurring_frequency;
    console.log('isRecurring', isRecurring);
    if (isRecurring) {
      alert('定期的なイベントを新しい日付にドラッグ アンド ドロップすることはできません。');
      return;
    }
    
    // Format new start and end times
    const newStartDate = moment(start).format('YYYY-MM-DD');
    const newEndDate = moment(end).format('YYYY-MM-DD');
    const newStartTime = moment(start).format('HH:mm');
    const newEndTime = moment(end).format('HH:mm');
    
    const updatedReservation = {
      id: reservationId,
      room_ids: reservation.room_ids,
      start_date: newStartDate,
      end_date: newEndDate,
      start_time: newStartTime,
      end_time: newEndTime,
    };

    handleUpdateReservation(updatedReservation);
  };

  return {
    handleEventDrop,
    handleEventResize
  };
};